import api from './api';

export interface EmailNotificationPreferences {
  interview_completed: boolean;
  interview_assigned: boolean;
  fraud_detected: boolean;
  system_alerts: boolean;
  weekly_summary: boolean;
}

export interface UserPreferences {
  id: string;
  user_id: string;

  // Appearance
  theme: 'light' | 'dark' | 'system';
  sidebar_collapsed: boolean;

  // Localization
  language: string;
  timezone: string;
  date_format: string;

  // Notifications
  notifications_enabled: boolean;
  email_notifications: Record<string, boolean>;
  sound_enabled: boolean;

  // Accessibility
  keyboard_shortcuts_enabled: boolean;
  reduce_animations: boolean;
  high_contrast: boolean;

  // Data
  table_page_size: number;
  dashboard_layout: Record<string, any>;
  default_filters: Record<string, any>;

  // Advanced
  compact_mode: boolean;
  show_tooltips: boolean;
  auto_save_enabled: boolean;

  // Timestamps
  created_at: string;
  updated_at: string;
}

export interface UserPreferencesUpdate {
  theme?: 'light' | 'dark' | 'system';
  sidebar_collapsed?: boolean;
  language?: string;
  timezone?: string;
  date_format?: string;
  notifications_enabled?: boolean;
  email_notifications?: Record<string, boolean>;
  sound_enabled?: boolean;
  keyboard_shortcuts_enabled?: boolean;
  reduce_animations?: boolean;
  high_contrast?: boolean;
  table_page_size?: number;
  dashboard_layout?: Record<string, any>;
  default_filters?: Record<string, any>;
  compact_mode?: boolean;
  show_tooltips?: boolean;
  auto_save_enabled?: boolean;
}

export interface PreferencesResetResponse {
  message: string;
  preferences: UserPreferences;
}

const preferencesService = {
  /**
   * Get current user preferences
   */
  getPreferences: async (): Promise<UserPreferences> => {
    const response = await api.get<UserPreferences>('/v1/preferences');
    return response.data;
  },

  /**
   * Update all user preferences (full update)
   */
  updatePreferences: async (preferences: UserPreferencesUpdate): Promise<UserPreferences> => {
    const response = await api.put<UserPreferences>('/v1/preferences', preferences);
    return response.data;
  },

  /**
   * Partially update user preferences
   */
  partialUpdatePreferences: async (preferences: UserPreferencesUpdate): Promise<UserPreferences> => {
    const response = await api.patch<UserPreferences>('/v1/preferences', preferences);
    return response.data;
  },

  /**
   * Reset preferences to defaults
   */
  resetPreferences: async (): Promise<PreferencesResetResponse> => {
    const response = await api.post<PreferencesResetResponse>('/preferences/reset');
    return response.data;
  },

  /**
   * Get default preferences
   */
  getDefaultPreferences: (): UserPreferencesUpdate => {
    return {
      theme: 'system',
      sidebar_collapsed: false,
      language: 'en',
      timezone: 'UTC',
      date_format: 'MM/DD/YYYY',
      notifications_enabled: true,
      email_notifications: {
        interview_completed: true,
        interview_assigned: true,
        fraud_detected: true,
        system_alerts: true,
        weekly_summary: true,
      },
      sound_enabled: true,
      keyboard_shortcuts_enabled: true,
      reduce_animations: false,
      high_contrast: false,
      table_page_size: 20,
      dashboard_layout: {},
      default_filters: {},
      compact_mode: false,
      show_tooltips: true,
      auto_save_enabled: true,
    };
  },
};

export default preferencesService;
